import React, { ReactElement, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useRootStore } from '~/stores/useRootStore';
import HBDropdown from '@honeybook/hbui/dist/components/HBDropdown/HBDropdown';
import styled from 'styled-components';
import HBCheckbox from '@honeybook/hbui/dist/components/HBCheckbox/HBCheckbox';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import colors from '@honeybook/hbui/dist/styles/colors';
import { useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import is from 'styled-is';
import HBClickable from '@honeybook/hbui/dist/components/HBClickable/HBClickable';
import { groupedDropdownCustomStyles } from '~/utils/automation.utils';
import TooltipWrapper from '~/common/TooltipWrapper';
import InfoCircle16 from '@honeybook/icons/dist/icons/InfoCircle16';

const TemplateConfigsDropDown = () => {
    const {
        companyStore: {
            currCompany: { flows }
        },
        automationUiStore: {
            isShowErrors,
            selectedStep: { isTemplateSelectionValid }
        }
    } = useRootStore();

    const { t } = useTranslation(NAMESPACES.AUTOMATIONS, {
        keyPrefix: 'sidebar.template_config_dropdown'
    });

    const hasError = isShowErrors && !isTemplateSelectionValid;

    const emailTemplatesForDropdown = flows
        .map(curr => ({
            value: curr._id,
            label: curr.title
        }))
        .sort((a, b) => {
            const isANumber = !isNaN(Number(a.label));
            const isBNumber = !isNaN(Number(b.label));

            if (isANumber && isBNumber) {
                return Number(a.label) - Number(b.label);
            }

            if (isANumber) {
                return 1;
            }
            if (isBNumber) {
                return -1;
            }
            return a.label.localeCompare(b.label);
        });

    return (
        <MainContainer>
            <MainTitle type="Body6DarkPrimary" hasError={hasError}>
                {hasError ? t('missing_template') : t('main_title')}
            </MainTitle>
            <HBDropdown
                size="sm"
                error={hasError}
                minMenuHeight={250}
                maxMenuHeight={250}
                options={emailTemplatesForDropdown}
                closeMenuOnSelect={false}
                customMenuListComponent={CustomMenuComponent}
                customOptionComponent={CustomOptionComponent}
                customNoOptionsComponent={CustomNoOptionsComponent}
                onChange={() => {}}
                placeholder=""
                customValueComponent={CustomValueComponent}
                customStyles={groupedDropdownCustomStyles}
                menuPlacement="auto"
                menuShouldScrollIntoView={false}
                testIdName="template-configs"
            />
        </MainContainer>
    );
};

export default observer(TemplateConfigsDropDown);

// --- CustomOptionComponent ---

const CustomOptionComponent = observer(({ data }) => {
    const {
        companyStore: {
            currCompany: { flows }
        },
        automationUiStore: {
            selectedStep: { triggerFilters, toggleTemplatesConfig }
        }
    } = useRootStore();

    const isSelected =
        triggerFilters?.template_ids?.includes(data.value) ||
        triggerFilters?.is_all_templates;

    const templateConfig = flows.find(curr => curr._id === data.value);

    return (
        <OptionContainer
            key={`dropdown_option_${templateConfig._id}`}
            onClick={() => toggleTemplatesConfig(templateConfig._id)}
            testIdName="toggle-template-config"
        >
            <HBText type="body14">{templateConfig.title}</HBText>
            <HBCheckbox
                size="xs"
                type="checkbox"
                checked={isSelected}
                onChange={() => {}}
                testIdName="template-config"
            />
        </OptionContainer>
    );
});

// --- CustomMenuComponent ---

interface CustomMenuComponentProps {
    isAllTypesSelected?: boolean;
    children?: ReactElement | ReactElement[];
    onClick?: () => void;
    innerRef: any;
    innerProps: JSX.IntrinsicElements['div'];
}

const CustomMenuComponent = observer(
    ({ children, innerRef, innerProps }: CustomMenuComponentProps) => {
        const {
            companyStore: { hasTeamMembers },
            automationUiStore: {
                selectedStep: { triggerFilters, toggleAllTemplatesConfigs }
            }
        } = useRootStore();

        const { t } = useTranslation(NAMESPACES.AUTOMATIONS, {
            keyPrefix: 'sidebar.template_config_dropdown'
        });

        const isAllTemplatesSelected = triggerFilters?.is_all_templates;
        return (
            <MenuContainer ref={innerRef} {...innerProps}>
                <AllItemsOptionContainer
                    onClick={() => toggleAllTemplatesConfigs()}
                    testIdName="toggle-all-template-configs"
                >
                    <TitleTooltipWrapper>
                        <HBText type="body14">{t('all_templates')}</HBText>
                        {hasTeamMembers && (
                            <TooltipWrapper
                                targetContainerStyle={{ display: 'flex' }}
                                maxWidth="204px"
                                placement="top"
                                tooltipText={t('all_templates_tooltip')}
                            >
                                <InfoCircle color={colors.neutral600} />
                            </TooltipWrapper>
                        )}
                    </TitleTooltipWrapper>
                    <HBCheckbox
                        size="xs"
                        type="checkbox"
                        checked={isAllTemplatesSelected}
                        onChange={() => {}}
                        testIdName="all-types"
                    />
                </AllItemsOptionContainer>
                {children}
            </MenuContainer>
        );
    }
);

// --- CustomValueComponent ---

interface CustomValueComponentProps {
    children?: ReactElement | ReactElement[];
}

const CustomValueComponent = observer(
    ({ children }: CustomValueComponentProps) => {
        const {
            automationUiStore: {
                selectedStep: { triggerFilters }
            },
            companyStore: {
                currCompany: { flows }
            }
        } = useRootStore();

        const { t } = useTranslation(NAMESPACES.AUTOMATIONS, {
            keyPrefix: 'sidebar.template_config_dropdown'
        });

        const selectedItemsLabels = flows
            .filter(Boolean)
            .filter(curr => triggerFilters?.template_ids?.includes(curr._id))
            .map(curr => curr.title);

        const hasItems =
            triggerFilters?.is_all_templates || !!selectedItemsLabels.length;

        const displayLabel = useMemo(() => {
            if (hasItems) {
                if (triggerFilters?.is_all_templates) {
                    return t('all_templates');
                } else {
                    if (selectedItemsLabels.length > 1) {
                        return t('items_title', {
                            count: selectedItemsLabels.length
                        });
                    } else {
                        return selectedItemsLabels.join(', ');
                    }
                }
            } else {
                return t('placeholder');
            }
        }, [
            triggerFilters?.is_all_templates,
            hasItems,
            selectedItemsLabels,
            t
        ]);

        return (
            <ValueContainer>
                <StyledText
                    type="body14"
                    color={hasItems ? 'textPrimary' : 'textTertiary'}
                >
                    {displayLabel}
                </StyledText>
                {children}
            </ValueContainer>
        );
    }
);

const CustomNoOptionsComponent = observer(() => {
    const { t } = useTranslation(NAMESPACES.AUTOMATIONS, {
        keyPrefix: 'sidebar.template_config_dropdown'
    });

    return <HBText type="body12">{t('empty_state')}</HBText>;
});

// -------------------------------- Styles -------------------------------- //

const OptionContainer = styled(HBClickable)`
    display: flex;
    justify-content: space-between;
    padding: 12px 24px;
    cursor: pointer;

    &:hover {
        background: ${colors.neutral100};
    }
`;

const MenuContainer = styled.div`
    cursor: auto;
`;

const AllItemsOptionContainer = styled(HBClickable)`
    display: flex;
    justify-content: space-between;
    padding: 12px 24px;
    border-bottom: 1px solid ${colors.neutral200};

    &:hover {
        background: ${colors.neutral100};
        cursor: pointer;
    }
`;

const TitleTooltipWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const ValueContainer = styled.div`
    align-items: center;
    display: flex;
    width: 100%;
`;

const MainTitle = styled(HBText)<{ hasError?: boolean }>`
    display: block;
    margin-bottom: 10px;
    ${is('hasError')`
        color: ${colors.red50};
    `}
`;

const MainContainer = styled.div`
    padding-bottom: 23px;
`;

const StyledText = styled(HBText)`
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const InfoCircle = styled(InfoCircle16)`
    display: flex;
`;
